import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import * as workTasksAPI from '../../services/workTasksAPI';
import type { WorkTask } from '../../services/workTasksAPI';

const WorkTaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [task, setTask] = useState<WorkTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completionComment, setCompletionComment] = useState('');
  const [completionPhoto, setCompletionPhoto] = useState<File | null>(null);
  const [completing, setCompleting] = useState(false);

  // RBAC Permissions
  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  const canEditTasks = isAdmin || isBauleiter;
  const canCompleteTask = task ? task.assigned_to_id === user?.id : false;

  useEffect(() => {
    if (id) {
      fetchTask();
    }
  }, [id]);

  const fetchTask = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await workTasksAPI.getWorkTask(parseInt(id));
      setTask(data);
    } catch (error) {
      console.error('Error fetching work task:', error);
      enqueueSnackbar('Fehler beim Laden des Arbeitsauftrags', { variant: 'error' });
      navigate('/work-tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!task) return;

    // Validate that photo is provided (now mandatory)
    if (!completionPhoto) {
      enqueueSnackbar('Ein Abschlussfoto ist erforderlich', { variant: 'error' });
      return;
    }

    try {
      setCompleting(true);
      await workTasksAPI.completeWorkTask(
        task.id,
        completionComment || undefined,
        completionPhoto
      );
      enqueueSnackbar('Arbeitsauftrag erfolgreich abgeschlossen', { variant: 'success' });
      setCompleteDialogOpen(false);
      fetchTask(); // Refresh task data
    } catch (error) {
      console.error('Error completing task:', error);
      enqueueSnackbar('Fehler beim Abschließen des Arbeitsauftrags', { variant: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCompletionPhoto(file);
    }
  };

  const handleDownloadAttachment = async () => {
    if (!task) return;
    try {
      await workTasksAPI.downloadAttachment(task.id);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      enqueueSnackbar('Fehler beim Herunterladen des Anhangs', { variant: 'error' });
    }
  };

  const handleDownloadCompletionPhoto = async () => {
    if (!task) return;
    try {
      await workTasksAPI.downloadCompletionPhoto(task.id);
    } catch (error) {
      console.error('Error downloading completion photo:', error);
      enqueueSnackbar('Fehler beim Herunterladen des Abschlussfotos', { variant: 'error' });
    }
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'hoch':
        return 'error';
      case 'mittel':
        return 'warning';
      case 'niedrig':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'neu':
        return 'primary';
      case 'erledigt':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'hoch':
        return 'Hoch';
      case 'mittel':
        return 'Mittel';
      case 'niedrig':
        return 'Niedrig';
      default:
        return priority;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'erledigt':
        return 'Erledigt';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!task) {
    return (
      <Alert severity="error">
        Arbeitsauftrag nicht gefunden.
      </Alert>
    );
  }

  return (
    <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/work-tasks')}
          >
            Zurück
          </Button>
          <Typography variant="h4" component="h1">
            {task.title}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {canEditTasks && task.status === 'neu' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/work-tasks/${task.id}/edit`)}
            >
              Bearbeiten
            </Button>
          )}
          {canCompleteTask && task.status === 'neu' && (
            <Button
              variant="contained"
              startIcon={<CompleteIcon />}
              onClick={() => setCompleteDialogOpen(true)}
            >
              Abschließen
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Typography variant="h5" component="h2">
                  Aufgabendetails
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip
                    label={getStatusLabel(task.status)}
                    color={getStatusColor(task.status) as any}
                  />
                  <Chip
                    label={getPriorityLabel(task.priority)}
                    color={getPriorityColor(task.priority) as any}
                  />
                </Box>
              </Box>

              {task.description && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Beschreibung
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {task.description}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Projekt
                  </Typography>
                  <Typography variant="body1">
                    {task.project_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Zugewiesen an
                  </Typography>
                  <Typography variant="body1">
                    {task.assigned_to_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Erstellt von
                  </Typography>
                  <Typography variant="body1">
                    {task.created_by_name}
                  </Typography>
                </Grid>
                {task.due_date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fälligkeitsdatum
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(task.due_date), 'dd.MM.yyyy', { locale: de })}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {task.attachment_filename && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Anhang
                  </Typography>
                  {isImageFile(task.attachment_filename) ? (
                    <Box>
                      <Box
                        component="img"
                        src={workTasksAPI.getAttachmentUrl(task.id)}
                        alt={task.attachment_filename}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                      <Box display="flex" gap={1}>
                        <Button
                          startIcon={<DownloadIcon />}
                          variant="outlined"
                          size="small"
                          onClick={handleDownloadAttachment}
                        >
                          Herunterladen
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          {task.attachment_filename}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Button
                      startIcon={<DownloadIcon />}
                      variant="outlined"
                      size="small"
                      onClick={handleDownloadAttachment}
                    >
                      {task.attachment_filename}
                    </Button>
                  )}
                </Box>
              )}

              {task.status === 'erledigt' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Abschluss
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Abgeschlossen am
                      </Typography>
                      <Typography variant="body1">
                        {task.completed_at && format(new Date(task.completed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {task.completion_comment && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Kommentar
                      </Typography>
                      <Typography variant="body1">
                        {task.completion_comment}
                      </Typography>
                    </Box>
                  )}

                  {task.completion_photo_filename && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Abschlussfoto
                      </Typography>
                      <Box>
                        <Box
                          component="img"
                          src={workTasksAPI.getCompletionPhotoUrl(task.id)}
                          alt="Abschlussfoto"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                        <Box display="flex" gap={1}>
                          <Button
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            size="small"
                            onClick={handleDownloadCompletionPhoto}
                          >
                            Herunterladen
                          </Button>
                          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            {task.completion_photo_filename}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Zeitstempel
              </Typography>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Erstellt
                </Typography>
                <Typography variant="body2">
                  {format(new Date(task.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Zuletzt aktualisiert
                </Typography>
                <Typography variant="body2">
                  {format(new Date(task.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Complete Task Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arbeitsauftrag abschließen</DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <TextField
              fullWidth
              label="Kommentar (optional)"
              value={completionComment}
              onChange={(e) => setCompletionComment(e.target.value)}
              multiline
              rows={4}
              margin="normal"
            />
          </Box>
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={completing}
              color={completionPhoto ? "success" : "primary"}
            >
              Foto anhängen (erforderlich)
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept="image/*"
                required
              />
            </Button>
            {completionPhoto ? (
              <Box mt={1}>
                <Typography variant="body2" color="success.main">
                  ✓ Ausgewähltes Foto: {completionPhoto.name}
                </Typography>
                {completionPhoto.type.startsWith('image/') && (
                  <Box
                    component="img"
                    src={URL.createObjectURL(completionPhoto)}
                    alt="Vorschau"
                    sx={{
                      maxWidth: '200px',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      mt: 1,
                    }}
                  />
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="error" mt={1}>
                Ein Foto ist für den Abschluss des Arbeitsauftrags erforderlich
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)} disabled={completing}>
            Abbrechen
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            disabled={completing || !completionPhoto}
            startIcon={completing ? <CircularProgress size={20} /> : <CompleteIcon />}
          >
            {completing ? 'Wird abgeschlossen...' : 'Abschließen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkTaskDetail;
